import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router-dom';
import { Card } from 'reactstrap';

import SectionTitle from '../../../../../../../components/Text/SectionTitle';
import { PathsLayouts } from '../../../../../../../config/routes';
import { TranslationsKeys } from '../../../../../../../locales/translations';
import { isNumber } from '../../../../../../../utils/math';
import CoursesBySemesters from './parts/CoursesBySemesters';
import { CurriculumLoader } from './parts/CurriculumLoader';
import Legends, { LegendsProps } from './parts/Legends';
import Resume, { ResumeProps } from './parts/Resume';
import { CourseStatusTooltip } from './types/progress';

type Props = {
  studyPlanEnrollmentIdStr: string | undefined;
};

export function Curriculum({ studyPlanEnrollmentIdStr }: Props) {
  const { t } = useTranslation(TranslationsKeys.STUDENT_FILE_ACADEMIC_HISTORY);
  const prefix = 'curriculumProgressStudent';

  const texts = useMemo(
    () => ({
      body: t(`${prefix}.body`),
      resumeTitle: t(`${prefix}.resume.title`),
      curriculumTitle: t(`${prefix}.curriculum.title`),
      credits: t(`${prefix}.curriculum.credits`),
      CurriculumProgressTooltip: t(`${prefix}.curriculumProgressTooltip`),
      CurriculumProgress: t(`${prefix}.curriculumProgress`),
      convalidationToolTip: ({
        validationType,
        validationSubType,
        examGrade,
        finalGrade,
        externalCourseName,
        externalGrade,
        code,
      }: CourseStatusTooltip) =>
        t(
          `${prefix}.curriculum.statusTooltip.${
            validationSubType || validationType
          }`,
          {
            examGrade,
            finalGrade,
            externalCourseName,
            externalGrade,
            code,
          },
        ),
      equivalentToTooltip: ({ code, name }: { code: string; name: string }) =>
        t(`${prefix}.curriculum.equivalentTo`, { code, name }),
      degreeActivityType: t(`${prefix}.curriculum.degreeActivityType`),
      degreeActivityTooltip: t(`${prefix}.curriculum.degreeActivityTooltip`),
      resume: {
        ...t<string, ResumeProps['texts']>(`${prefix}.resume`, {
          returnObjects: true,
        }),
        durationValue: (duration: number, periodType: string) =>
          t(`${prefix}.resume.durationValue`, { count: duration, periodType }),
      },
      curriculumStatus: t<string, LegendsProps['texts']>(
        `${prefix}.curriculum.status`,
        {
          returnObjects: true,
        },
      ),
      progress: {
        table: {
          columns: {
            section: t(`${prefix}.progress.section`),
            courseCode: t(`${prefix}.progress.courseCode`),
            generalPassedAverage: t(`${prefix}.progress.generalPassedAverage`),
            averageGradeToDate: t(`${prefix}.progress.averageGradeToDate`),
            progressPorcentInGrades: t(
              `${prefix}.progress.progressPorcentInGrades`,
            ),
            averageAttendanceToDate: t(
              `${prefix}.progress.averageAttendanceToDate`,
            ),
            attendanceCatedral: t(`${prefix}.progress.attendanceCatedral`),
            attendanceLaboratory: t(`${prefix}.progress.attendanceLaboratory`),
            attendanceAssistantship: t(
              `${prefix}.progress.attendanceAssistantship`,
            ),
          },
          tooltips: {
            attendanceCatedral: t(
              `${prefix}.progress.tooltip.attendanceCatedral`,
            ),
            attendanceLaboratory: t(
              `${prefix}.progress.tooltip.attendanceLaboratory`,
            ),
            attendanceAssistantship: t(
              `${prefix}.progress.tooltip.attendanceAssistantship`,
            ),
          },
          notResult: {
            title: t(`${prefix}.progress.notResult.title`),
            subtitle: t(`${prefix}.progress.notResult.subtitle`),
          },
        },
      },
    }),
    [t],
  );

  const studyPlanEnrollmentId = useMemo(() => {
    if (isNumber(studyPlanEnrollmentIdStr)) {
      return studyPlanEnrollmentIdStr;
    }
    return null;
  }, [studyPlanEnrollmentIdStr]);

  if (!studyPlanEnrollmentId) {
    return <Redirect to={PathsLayouts.studentFile} />;
  }

  return (
    <Card className="p-4 mt-2 mx-3">
      <CurriculumLoader studyPlanEnrollmentId={studyPlanEnrollmentId}>
        {({ data }) => (
          <>
            {/* DATOS DE LA MALLA */}
            <div className="mb-4">
              <div className="mb-3">
                <SectionTitle text={texts.resumeTitle} />
              </div>
              <Resume
                texts={texts.resume}
                {...data.progress}
                periodTypeName={data.progress.studyPlanVersion.studyPlan?.periodType?.name.trim()}
                periodTypeNamePlural={data.progress.studyPlanVersion.studyPlan?.periodType?.namePlural.trim()}
              />
            </div>

            {/* MALLA DE ESTUDIOS */}
            <div>
              <div className="mb-3">
                <SectionTitle text={texts.curriculumTitle} />
              </div>
              <div className="mb-3">
                <CoursesBySemesters
                  extraCourses={data.extraCourses}
                  semesters={data.progress.semesters}
                  texts={texts}
                />
              </div>
              <Legends texts={texts.curriculumStatus} />
            </div>
          </>
        )}
      </CurriculumLoader>
    </Card>
  );
}
